"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.ICON_ADDITIONS = exports.ICON_NAMES = void 0;
exports.Icon = Icon;
const jsx_runtime_1 = require("react/jsx-runtime");
const lucide = __importStar(require("lucide-react"));
const cn_1 = require("../lib/cn");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One glyph, from one closed set.                                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The design system ships **one icon set: a 77-glyph lucide subset**, listed in
 * `docs/design-system/assets/icons.json`. Not "lucide" — that subset. The rule it states is
 * blunt: *"No second icon family — no Heroicons, no icon font, no CDN set with a different
 * stroke weight. If a glyph is missing, add it to the set rather than importing another
 * library."*
 *
 * ⚠️ **`ICON_NAMES` is that list, and it is the type.** `IconName` is a union of 78 string
 * literals, so a glyph outside the set is a **compile error at the call site**, not a blank
 * square at runtime. The design system's own implementation says "unknown names render empty",
 * which is the failure this project is worst at: it compiles, it loads, and a toolbar button
 * has no glyph until somebody looks at that screen.
 *
 * ⚠️ **The name list is duplicated from the vendored JSON on purpose, and a test compares
 * them.** It has to be a literal to be a type — TypeScript cannot read a JSON file into a
 * union — so `src/__tests__/icon.test.tsx` asserts the two agree in both directions, and that
 * every one of the 77 actually resolves in `lucide-react`. Importing the JSON instead would
 * give a `string` type and lose the whole point.
 */
exports.ICON_NAMES = [
    'arrow-right',
    'at-sign',
    'bell',
    'building',
    'calendar',
    'calendar-days',
    'chart-column',
    'check',
    'chevron-down',
    'chevron-left',
    'chevron-right',
    'chevron-up',
    'chevrons-up-down',
    'circle',
    'circle-check',
    'circle-alert',
    'circle-dot',
    'circle-help',
    'circle-plus',
    'clock',
    'command',
    'copy',
    'credit-card',
    'database',
    'download',
    'ellipsis',
    /* The password field's reveal — `ICON_ADDITIONS` says why. */
    'eye',
    'eye-off',
    'file-spreadsheet',
    'file-text',
    'flag',
    'folder',
    'funnel',
    'globe',
    'grip-vertical',
    'home',
    'inbox',
    'kanban',
    'key',
    'languages',
    'layout-grid',
    /* C19's comment editor. Every one of these is a `lucide-react` export reached by `pascal()` —
     * adding a name here is the whole of adding an icon. */
    'bold',
    'italic',
    'strikethrough',
    'code',
    'heading-2',
    'list',
    'list-ordered',
    'quote',
    'braces',
    'undo',
    'redo',
    'link',
    'list-checks',
    'log-out',
    'mail',
    'maximize',
    'message-square',
    'minimize',
    'monitor',
    'moon',
    'panel-left',
    'panel-right',
    'paperclip',
    'pencil',
    'play',
    'plug',
    'palette',
    'plus',
    'qr-code',
    'refresh-cw',
    'repeat',
    'search',
    'send-horizontal',
    'settings',
    'shield-check',
    'sliders-horizontal',
    'smartphone',
    'sparkles',
    'square',
    'square-dashed',
    'star',
    'sun',
    'tag',
    'timer',
    'trash',
    'triangle-alert',
    'upload',
    'user-round',
    'users',
    'webhook',
    'x',
];
/**
 * ⚠️ **GLYPHS WE ADDED, LISTED SEPARATELY FROM THE ONES WE WERE GIVEN.**
 *
 * `docs/design-system/assets/icons.json` is **vendored** — an external authority, not ours to edit
 * — so an addition cannot be made by appending to it. It also cannot be made silently: the point of
 * a closed set is that a second visual language never creeps in, and `__tests__/icon.test.tsx`
 * compares `ICON_NAMES` against the vendored list *plus this array*, so anything else is a failure.
 *
 * The bar for a row here: **the mockup draws this glyph and the design system does not ship it.**
 * Substituting a different icon would diverge visibly from a screen the mockup does draw, and
 * inventing one would be the second language. Adding a *lucide* icon to a *lucide* subset is within
 * the spirit; the justification is what stops that reasoning from covering everything.
 *
 *  * `palette` — the Theme tab in account settings (`main.jsx`'s `navDef`, C6).
 */
exports.ICON_ADDITIONS = [
    'palette',
    /*
     * ⚠️ **Eleven at once, and they are ONE addition rather than eleven.** R-16's comment editor
     * has a formatting toolbar, and a formatting toolbar is bold, italic, strike, code, a heading, two
     * list kinds, a quote, a code block, and undo/redo. There is no subset of these that makes a
     * usable toolbar, and none of them has any other caller — they arrived together and they would
     * leave together. The design system's `icons.json` predates the editor and does not carry them.
     */
    'bold',
    'italic',
    'strikethrough',
    'code',
    'heading-2',
    'list',
    'list-ordered',
    'quote',
    'braces',
    'undo',
    'redo',
    /*
     * ⚠️ **Below the bar above, and flagged as such: the mockup draws no password reveal.** Asked for
     * by name after a sign-in failure on UAT (17 Oct 2026) — `PasswordInput` in `field.tsx` is the
     * only caller. Both or neither: the eye shows the state you would switch to.
     */
    'eye',
    'eye-off',
    /*
     * ⚠️ **Below the bar above, and flagged as such: asked for by name** (30 Oct 2026, the product
     * owner) — the *Idő* row's short-day mark, amber, beside the red `circle-help` of hours on an
     * unknown line item. Lucide, so the same stroke as the rest of the set.
     */
    'circle-alert',
];
/** `chevron-down` → `ChevronDown`, which is how `lucide-react` names its exports. */
function pascal(name) {
    return name
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
}
const GLYPHS = Object.fromEntries(exports.ICON_NAMES.map((name) => [name, lucide[pascal(name)]]));
/**
 * ⚠️ **`aria-hidden` by default, and that is the correct default rather than a shortcut.** An
 * icon beside a label is decoration and naming it makes a screen reader say the same thing
 * twice. An icon that is the ONLY label needs its *control* named — `aria-label` on the button,
 * not on the glyph — because that is what the accessibility tree exposes as clickable.
 *
 * ⚠️ **Status and priority are NOT icons in this product.** They are ring and dot geometry plus
 * colour, drawn by the list and timeline cells. Reaching for a glyph to mean "blocked" puts a
 * second visual language beside the one the whole app uses.
 */
function Icon({ name, size = 16, strokeWidth = 2, className }) {
    const Glyph = GLYPHS[name];
    /* Unreachable through the type, and still handled: a JS caller, a `as IconName` cast, or a
     * lucide upgrade that renames an export all land here. Returning null silently is what the
     * design system does and is what this component exists not to do — but throwing in render
     * would take a whole screen down for a missing 14px glyph. The reserved box keeps the layout
     * and `src/__tests__/icon.test.tsx` makes the case unreachable in practice. */
    if (Glyph === undefined) {
        return ((0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('inline-block', className), style: { width: size, height: size }, "data-unknown-icon": name }));
    }
    return ((0, jsx_runtime_1.jsx)(Glyph, { width: size, height: size, strokeWidth: strokeWidth, className: (0, cn_1.cn)('shrink-0', className), "aria-hidden": true, focusable: false }));
}
//# sourceMappingURL=icon.js.map